import type { IFinancialPlanQuality } from 'partner360/features/financialPlanQuality'

import type { IPlanningMeasureQuality } from '../model/planningMeasure'
import { PlanReviewSection, TPlanReviewQualitySection } from '../model/types'
import type { TInitiativesPlanQuality } from '../model/usePlanReviewQuality'

import { isBelowHighQuality } from './isBelowHighQuality'

interface IGetRequiredJustificationSectionsParams {
  financialQuality: Pick<IFinancialPlanQuality, 'financialGrowth' | 'sobGrowth'>
  hasFinancialPlan: boolean
  initiativesQuality: TInitiativesPlanQuality
  planningMeasureQuality?: IPlanningMeasureQuality
  isPlanningMeasureEnabled?: boolean
}

/**
 * A justification belongs to the whole quality pillar, not to its detail rows.
 * Pillars without a score and financial pillars without a plan do not require one.
 */
export const getRequiredJustificationSections = ({
  financialQuality,
  hasFinancialPlan,
  initiativesQuality,
  planningMeasureQuality,
  isPlanningMeasureEnabled = false,
}: IGetRequiredJustificationSectionsParams): TPlanReviewQualitySection[] => {
  const sections: TPlanReviewQualitySection[] = []

  if (hasFinancialPlan && isBelowHighQuality(financialQuality.financialGrowth.band)) {
    sections.push(PlanReviewSection.FinancialGrowth)
  }
  if (hasFinancialPlan && isBelowHighQuality(financialQuality.sobGrowth.band)) {
    sections.push(PlanReviewSection.SobGrowth)
  }
  if (isPlanningMeasureEnabled && planningMeasureQuality?.band === 'Low') {
    sections.push(PlanReviewSection.PlanningMeasure)
  }
  if (isBelowHighQuality(initiativesQuality.financialPlanCoverage.band)) {
    sections.push(PlanReviewSection.FinancialPlanCoverage)
  }
  if (isBelowHighQuality(initiativesQuality.descriptionQuality.band)) {
    sections.push(PlanReviewSection.DescriptionQuality)
  }
  if (isBelowHighQuality(initiativesQuality.strategicGrowthAreas.band)) {
    sections.push(PlanReviewSection.StrategicGrowthAreas)
  }

  return sections
}
